import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { latestAttr, humanizeToken, registryOptions, type AttrMap } from '../lib/inlineAttribution';
import { resolveStateKey } from '../lib/us-states';
import { valueFaceLabel } from '../lib/valueLabel';
import { filterForums, filterStatusOptions, stateNameOf, useStateTerminology } from '../lib/stateTerminology';
import { Badge, Button } from '../atoms';
import { StatCard, InlineEditCard, LastUpdated, ParcelValueExemptionsPanel, ParcelValueContextsPanel, useToast } from '../organisms';
import { EntityHeader } from '../shell';
import { formatDate } from '../lib/orgTimezone';
import { useEntityPermission, usePermissions } from '../lib/usePermissions';

/**
 * ParcelValueDetail — /parcel-values/:id
 *
 * The value "card for the tax year" (B1). A parcel's Values tab lists one card
 * per tax year; clicking a value row lands here to view + edit that single
 * parcel_values row in place — market / assessed / per-unit / cap figures, each
 * box inline-editable with its own field attribution. Replaces the dead
 * `/parcel-values/:id → /parcels` redirect that made the row click a no-op.
 *
 * tax_year + value_type are immutable (set at creation) so they live in the
 * header, not an editable box.
 */

interface ParcelValue {
  id: string;
  parcel_id: string;
  tax_year: number;
  value_type: string;
  status: string | null;
  value_source: string | null;
  date: string | null;
  is_beginning_value: boolean | number | null;
  is_current_value: boolean | number | null;
  market_land: number | null;
  market_imp: number | null;
  market_pp: number | null;
  market_total: number | null;
  assessed_land: number | null;
  assessed_imp: number | null;
  assessed_pp: number | null;
  assessed_total: number | null;
  land_per_sf: number | null;
  imp_per_sf: number | null;
  val_per_sf: number | null;
  val_per_unit: number | null;
  capped_value: number | null;
  cap_year: number | null;
  new_improvements_value: number | null;
  alternate_value: number | null;
  alternate_value_type: string | null;
  // §2.1 — the forum + event this value happened in. NULL on every row written
  // before those columns existed, which is why the label falls back to value_type.
  forum: string | null;
  event: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  // ATA part 3 §6 glass case: Audit Complete (internal go-live) then Share with Client (portal push).
  audit_completed_at?: string | null;
  audit_completed_by_user_id?: string | null;
  shared_to_portal_at?: string | null;
  shared_to_portal_by_user_id?: string | null;
}

// `state` is the JURISDICTIONAL state the appeal ladder resolves off; `parcel_state`
// is part of the mailing-address block and is empty on most parcels.
interface Parcel { id: string; account_number: string; name: string | null; state?: string | null; parcel_state?: string | null; }

/** An appeal option for the classification Appeal selector (§4.3). */
interface AppealOption { id: string; tax_year: number | null; lifecycle_stage: string }

/** The stored stage-templates shape (GET /api/org-settings/stage-templates). */
interface StageEvent { name: string }
interface StageForum { name: string; events: StageEvent[]; retired?: boolean }
type StageTemplates = Record<string, { forums: StageForum[] }>;

function money(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : `$${n.toLocaleString()}`;
}
// Assessment ratio as a percentage (stored decimal 1.0000 → "100%") — B1.
function pct(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}
const isOn = (v: unknown): boolean => v === true || v === 1 || v === '1';

export function ParcelValueDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const valueQuery = useQuery<ParcelValue>({
    queryKey: ['parcel-value', id],
    queryFn: ({ signal }) => api.get<ParcelValue>(`/api/parcel-values/${id}`, signal),
    enabled: !!id,
  });
  const val = valueQuery.data;

  const parcelQuery = useQuery<Parcel>({
    queryKey: ['parcel', val?.parcel_id],
    queryFn: ({ signal }) => api.get<Parcel>(`/api/parcels/${val!.parcel_id}`, signal),
    enabled: !!val?.parcel_id,
  });
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'parcel_values', id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/parcel_values/${id}`, signal),
    enabled: !!id,
  });
  // 7/26 A1 — the parcel-value Status is an org-configurable dropdown.
  const statusesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'parcel-value-statuses'],
    queryFn: ({ signal }) => api.get('/api/org-settings/parcel-value-statuses', signal),
    staleTime: 5 * 60 * 1000,
  });
  // §2.8 — the alternate-value TYPE selector is fed by the §2.7 reduction-types
  // registry (the same global list that drives a reduction's type). GET returns
  // an array of { name } entries; map each name to a select option.
  const reductionTypesQuery = useQuery<{ name: string }[]>({
    queryKey: ['org-settings', 'reduction-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/reduction-types', signal),
    staleTime: 5 * 60 * 1000,
  });
  // §4.3 — the Appeal selector lists the parcel's appeals; Forum/Event come from
  // the state's stage template. Both are gated on the value being loaded.
  const appealsQuery = useQuery<AppealOption[]>({
    queryKey: ['appeals-by-parcel', val?.parcel_id],
    queryFn: ({ signal }) => api.get<AppealOption[]>(`/api/appeals?parcel_id=${val!.parcel_id}`, signal),
    enabled: !!val?.parcel_id,
  });
  const templatesQuery = useQuery<StageTemplates>({
    queryKey: ['org-settings', 'stage-templates'],
    queryFn: ({ signal }) => api.get<StageTemplates>('/api/org-settings/stage-templates', signal),
    staleTime: 5 * 60 * 1000,
  });
  // Spec 0012 — the parcel's state terminology: value-type/status allowlists + appeal labels.
  const terminology = useStateTerminology(parcelQuery.data?.state ?? parcelQuery.data?.parcel_state).data;
  // C1 — exemptions on the value record are gated on parcel_value edit (default-deny).
  const canEditValue = useEntityPermission('parcel_value', 'can_edit');
  // §6 glass case: only an administrator releases a value, in two logged steps.
  const role = usePermissions().data?.user?.role;
  const canRelease = role === 'super_admin' || role === 'admin' || role === 'administrator';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const release = useMutation({
    mutationFn: (step: 'audit-complete' | 'share-with-client') => api.post<ParcelValue>(`/api/value-release/${id}/${step}`, {}),
    onSuccess: (_row, step) => {
      queryClient.invalidateQueries({ queryKey: ['parcel-value', id] });
      toast({ variant: 'success', message: step === 'audit-complete' ? 'Audit complete. The value can now be shared with the client.' : 'Shared with the client. It is visible in their portal.' });
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  if (valueQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading value…</p>;
  }
  if (valueQuery.error) {
    const notFound = valueQuery.error instanceof ApiClientError && valueQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Value not found' : 'Failed to load value'}</h2>
        <p style={{ margin: 0 }}>{(valueQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>← Back</Button>
        </p>
      </div>
    );
  }
  if (!val) return null;

  const attr: AttrMap = attrQuery.data ?? {};
  const record = val as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [
    ['parcel-value', val.id],
    ['field-attribution', 'parcel_values', val.id],
    ['parcel-values', val.parcel_id],
  ];
  const backToParcel = () => navigate(`/parcels/${val.parcel_id}`);

  // §2.8 — the alternate-value TYPE options come from the §2.7 reduction-types
  // registry (map each { name } to an option). The InlineEditCard keeps a stored
  // value that is no longer in the list selectable (current-value-prepend).
  const reductionTypeOptions = [
    { value: '', label: '— Select —' },
    ...(reductionTypesQuery.data ?? []).map((t) => ({ value: t.name, label: t.name })),
  ];
  // The alternate-value panel HEADING follows the selected type (falling back to a
  // neutral default when none is set), so the same panel serves an abatement, a
  // freeport value or a phase-in value.
  const alternateValueHeading = val.alternate_value_type || 'Alternate value';

  // §4.3 — Appeal / Forum / Event options for the classification panel.
  const appeals = appealsQuery.data ?? [];
  const appealById = new Map(appeals.map((a) => [a.id, a] as const));
  const appealLabel = (id: unknown): string => {
    const a = id ? appealById.get(String(id)) : null;
    return a ? `${a.tax_year ?? '—'} appeal · ${a.lifecycle_stage.replace(/_/g, ' ')}` : (id ? String(id) : '—');
  };
  const appealOptions = [
    { value: '', label: '— No appeal —' },
    ...appeals.map((a) => ({ value: a.id, label: `${a.tax_year ?? '—'} appeal · ${a.lifecycle_stage.replace(/_/g, ' ')}` })),
  ];
  // Resolve the parcel's state ladder (by full name or postal code), then the
  // forum names + the union of every forum's events for the Forum/Event selects.
  const templates = templatesQuery.data;
  const stateKey = templates
    ? resolveStateKey(Object.keys(templates), parcelQuery.data?.state ?? parcelQuery.data?.parcel_state)
    : null;
  const stateForums = (stateKey && templates ? templates[stateKey].forums : []).filter((f) => !f.retired);
  // Spec 0012: the Forum choices and the Status choices narrow to what the parcel's state
  // allows. The events list keeps the full ladder so a forum stored before the allowlist still
  // finds its events; a stored status outside the list stays selectable, marked as such.
  const parcelState = parcelQuery.data?.state ?? parcelQuery.data?.parcel_state;
  const forumOptions = [{ value: '', label: '— Select —' }, ...filterForums(stateForums, terminology?.allowed_value_types).map((f) => ({ value: f.name, label: f.name }))];
  const statusOptions = filterStatusOptions(registryOptions(statusesQuery.data, { preserveOrder: true }), terminology?.allowed_value_statuses, val.status, stateNameOf(parcelState));
  /**
   * Events for the forum CURRENTLY selected in the card — not the union of every
   * forum's events. A forum and its events are one vocabulary: a District Court
   * `petition filed` is not something an ARB can do, and offering it produced
   * values labelled with a forum/event pair that never happens.
   *
   * ⚠️ Falls back to the full union when no forum is chosen (so an untagged legacy
   * row can still be labelled) and when the forum is one the ladder does not carry
   * (a template edited since the row was written) — narrowing to an empty list
   * there would strand the row with no way to describe it.
   */
  const eventOptionsFor = (draft: Record<string, unknown>) => {
    const chosen = String(draft.forum ?? '');
    const match = stateForums.find((f) => f.name === chosen);
    const names = match
      ? match.events.map((e) => e.name)
      : [...new Set(stateForums.flatMap((f) => f.events.map((e) => e.name)))];
    return [{ value: '', label: '— Select —' }, ...names.map((name) => ({ value: name, label: name }))];
  };

  return (
    <>
      <EntityHeader
        prefix={`Parcel value · ${val.tax_year}`}
        title={valueFaceLabel(val, terminology)}
        subtitle={
          <>
            {parcelQuery.data
              ? <a href={`/parcels/${val.parcel_id}`} onClick={(e) => { e.preventDefault(); backToParcel(); }} style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)' }}>
                  {parcelQuery.data.account_number}{parcelQuery.data.name ? ` — ${parcelQuery.data.name}` : ''} ↗
                </a>
              : null}
            <LastUpdated updatedBy={val.updated_by} updatedAt={val.updated_at} />
          </>
        }
        statusPills={
          <>
            {val.status && <Badge variant="neutral">{humanizeToken(val.status)}</Badge>}
            {isOn(val.is_current_value) && <Badge variant="success" dot>Current</Badge>}
            {isOn(val.is_beginning_value) && <Badge variant="info">Beginning</Badge>}
            {val.audit_completed_at && <Badge variant="info" title={`Audit complete ${formatDate(val.audit_completed_at)}`}>Audited</Badge>}
            {val.shared_to_portal_at && <Badge variant="success" title={`Shared ${formatDate(val.shared_to_portal_at)}`}>Shared with client</Badge>}
          </>
        }
        actions={
          <>
            {canRelease && !val.audit_completed_at && (
              <Button variant="primary" size="sm" loading={release.isPending} onClick={() => release.mutate('audit-complete')}>Audit Complete</Button>
            )}
            {canRelease && val.audit_completed_at && !val.shared_to_portal_at && (
              <Button variant="primary" size="sm" loading={release.isPending} onClick={() => release.mutate('share-with-client')}>Share with Client</Button>
            )}
            <Button variant="secondary" size="sm" onClick={backToParcel}>← Back to parcel</Button>
          </>
        }
        kpis={
          <>
            <StatCard label="Market total" value={money(val.market_total)} caption={`Land ${money(val.market_land)}`} accent="hunter" />
            <StatCard label="Assessed total" value={money(val.assessed_total)} caption={`Land ${money(val.assessed_land)}`} accent="info" />
            <StatCard label="Improvement (market)" value={money(val.market_imp)} caption={val.market_pp != null ? `Personal ${money(val.market_pp)}` : 'No personal'} accent="amber" />
            <StatCard label="Value / SF" value={money(val.val_per_sf)} caption={val.val_per_unit != null ? `${money(val.val_per_unit)} / unit` : 'No per-unit'} accent="green" />
          </>
        }
      />

      {/* B1 — market → ratio → assessed STACKED within the record. At a 100% ratio
          market and assessed are identical, so side-by-side reads as duplication;
          stacking with the ratio between shows how assessed derives from market. */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
        maxWidth: 520, marginBottom: 'var(--sp-5)',
      }}>
        <InlineEditCard
          title="Market value"
          routePath="parcel-values"
          recordId={val.id}
          record={record}
          canEdit={canEditValue}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['market_land', 'market_imp', 'market_pp', 'market_total'])}
          fields={[
            { key: 'market_land', label: 'Land', type: 'number', display: money },
            { key: 'market_imp', label: 'Improvement', type: 'number', display: money },
            { key: 'market_pp', label: 'Personal property', type: 'number', display: money },
            { key: 'market_total', label: 'Total', type: 'number', display: money },
          ]}
        />
        <InlineEditCard
          title="Assessment ratio"
          routePath="parcel-values"
          recordId={val.id}
          record={record}
          canEdit={canEditValue}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['ratio_land', 'ratio_imp', 'ratio_pp'])}
          fields={[
            { key: 'ratio_land', label: 'Land', type: 'number', display: pct },
            { key: 'ratio_imp', label: 'Improvement', type: 'number', display: pct },
            { key: 'ratio_pp', label: 'Personal property', type: 'number', display: pct },
          ]}
        />
        <InlineEditCard
          title="Assessed value"
          routePath="parcel-values"
          recordId={val.id}
          record={record}
          canEdit={canEditValue}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['assessed_land', 'assessed_imp', 'assessed_pp', 'assessed_total'])}
          fields={[
            { key: 'assessed_land', label: 'Land', type: 'number', display: money },
            { key: 'assessed_imp', label: 'Improvement', type: 'number', display: money },
            { key: 'assessed_pp', label: 'Personal property', type: 'number', display: money },
            { key: 'assessed_total', label: 'Total', type: 'number', display: money },
          ]}
        />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-5)',
      }}>
        {/* §2.8 — the alternate value: a PARALLEL figure that sits alongside the
            assessed value on the record, NOT a subtraction and NOT part of the tax
            cascade. The same panel serves an abatement / freeport / phase-in value —
            its heading is driven by the selected TYPE (from the reduction-types
            registry) rather than a hardcoded "Abatement". */}
        <InlineEditCard
          title={alternateValueHeading}
          routePath="parcel-values"
          recordId={val.id}
          record={record}
          canEdit={canEditValue}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['alternate_value_type', 'alternate_value'])}
          fields={[
            { key: 'alternate_value_type', label: 'Type', type: 'select', options: reductionTypeOptions, display: (v) => (v ? String(v) : '—') },
            { key: 'alternate_value', label: 'Value', type: 'number', display: money, hint: 'A parallel figure alongside the assessed value — it does not feed the tax cascade (§2.8).' },
          ]}
        />
        <InlineEditCard
          title="Classification"
          routePath="parcel-values"
          recordId={val.id}
          record={record}
          canEdit={canEditValue}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['appeal_id', 'forum', 'event', 'status', 'value_source', 'date', 'is_current_value'])}
          fields={[
            // §4.3 — a value IS an appeal event: link the appeal + carry the
            // stage-template forum + event labels. Status stays (its own dropdown);
            // Source is required; the savings baseline is the renamed beginning value.
            { key: 'appeal_id', label: 'Appeal', type: 'select', options: appealOptions, preserveOrder: true, display: appealLabel },
            { key: 'forum', label: 'Forum', type: 'select', options: forumOptions, preserveOrder: true, display: (v) => (v ? String(v) : '—') },
            { key: 'event', label: 'Event', type: 'select', options: eventOptionsFor, preserveOrder: true, display: (v) => (v ? String(v) : '—') },
            { key: 'status', label: 'Status', type: 'select', options: statusOptions, preserveOrder: true, addOptionSlug: 'parcel-value-statuses', display: (v) => (v ? humanizeToken(String(v)) : '—') },
            { key: 'value_source', label: 'Source', type: 'text', required: true },
            { key: 'date', label: 'As-of date', type: 'date', display: (v) => v ? formatDate(String(v)) : '—' },
            { key: 'is_current_value', label: 'Current value', type: 'checkbox' },
          ]}
        />
        {/* §4 — the cap fields have moved off this tab. A cap is a property of how a
            jurisdiction taxes the parcel, not a property of the value, so it belongs on
            the jurisdiction-line record (Tax Calculation Method · Cap · Starts · Ends).
            `capped_value` / `cap_year` stay on this ROW — the §23.23 rule derives them and
            applyValueCap reads them upstream of the ratio — they are simply no longer
            hand-editable here. They surface read-only on the jurisdiction line, labelled as
            the parcel-level statutory clamp so it is not confused with the line's own cap.
            `new_improvements_value` stays: it is an input to that derivation, not a cap. */}
        <InlineEditCard
          title="Per-unit values"
          routePath="parcel-values"
          recordId={val.id}
          record={record}
          canEdit={canEditValue}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['val_per_sf', 'val_per_unit', 'land_per_sf', 'imp_per_sf', 'new_improvements_value'])}
          fields={[
            { key: 'val_per_sf', label: 'Value / SF', type: 'number', display: money },
            { key: 'val_per_unit', label: 'Value / unit', type: 'number', display: money },
            { key: 'land_per_sf', label: 'Land / SF', type: 'number', display: money },
            { key: 'imp_per_sf', label: 'Improvement / SF', type: 'number', display: money },
            { key: 'new_improvements_value', label: 'New improvements', type: 'number', display: money },
          ]}
        />
      </div>

      {/* §1.3 — phase-in and 299(c) live HERE, at the parcel value, alongside the
          exemptions below. Both were one object already (a freeze and a phase-in
          differ only by `kind`), so the standalone Phase-in tab on the parcel
          profile is gone rather than duplicated. */}
      <div style={{ marginBottom: 'var(--sp-5)' }}>
        <ParcelValueContextsPanel parcelId={val.parcel_id} taxYear={val.tax_year} />
      </div>

      {/* §1.1 — EXEMPTIONS on this value record (land / improvement / both, optionally
          scoped to one jurisdiction line) flow into the tax estimate; only the current
          value row moves the live bill. The panel reads the value's market/assessed
          figures for the "show the math" readout and sources its jurisdiction-line
          dropdown from this parcel's lines for the value's year. */}
      <div style={{ maxWidth: 720, marginBottom: 'var(--sp-5)' }}>
        <ParcelValueExemptionsPanel
          parcelValueId={val.id}
          isCurrentValue={isOn(val.is_current_value)}
          canEdit={canEditValue}
          invalidateKeys={[...invalidate, ['tax-bills'], ['parcels']]}
          parcelId={val.parcel_id}
          taxYear={val.tax_year}
          marketTotal={val.market_total}
          assessedTotal={val.assessed_total}
          marketLand={val.market_land}
          marketImp={val.market_imp}
          assessedLand={val.assessed_land}
          assessedImp={val.assessed_imp}
        />
      </div>
    </>
  );
}
